/**
 * src/state/actions/optimisticRollback.js
 *
 * Rollback helpers for the optimistic-update-then-persist shape used by the
 * store-backed registry writers (assets, BIA, vendors).
 *
 * Why these collections need it and the blob-backed ones do not
 * ------------------------------------------------------------
 * `assets`, `biaAssessments` and `vendors` are authoritative in their own
 * per-record store tables (ADR-0002 / ADR-0005) and are STRIPPED from the
 * tenant_state blob by `state-write` once the tenant's store row exists. So a
 * record that only ever made it into local state is not merely unsaved — it is
 * unsaveable: the debounced blob write still succeeds, the server drops the
 * key, and none of `usePersistence`'s retry / write-error-banner machinery ever
 * fires, because from its point of view nothing failed.
 *
 * The writer optimistically inserts the record for immediate UI feedback and
 * then awaits the per-record write. When that write fails (offline, 5xx, a
 * genuine 409), the caller's dialog surfaces the error — but the optimistic
 * record stays in state. The user is looking at a list containing a row the
 * server has never heard of, which disappears at the next reload. That is a
 * false success signal, and it is what #4991 reports.
 *
 * Rolling the single touched record back the moment its write fails keeps the
 * list honest: the error the caller already surfaces becomes the whole truth.
 * The rollback is deliberately per-record rather than a whole-array snapshot,
 * so a concurrent edit to a DIFFERENT record made while the write was in flight
 * is not undone along with it.
 *
 * Write fences — a rollback must not undo a SIBLING write that landed
 * -----------------------------------------------------------------
 * Per-record is not fine-grained enough on its own. Two writes for the SAME
 * record id can be in flight at once — a draft autosave racing a terminal
 * save, a dialog action racing the network-retry queue's replay, a double
 * submit. They race the store's per-record version check, so one of them is
 * refused. The refused one then rolls back using a `before` snapshot captured
 * before EITHER call ran, and for a call that believed it was creating the
 * record that snapshot is `null` — which means "remove it again".
 *
 * So the loser deletes the record from local state having no idea its sibling
 * had, in the meantime, committed the real thing and reconciled the server's
 * copy in. The server holds a valid row; the list does not show it; the user
 * is told their record was not saved when it was. That is TRYGGR_INTENT.md
 * §6.2 (reporting an outcome the data does not support) and §10 (two surfaces
 * disagreeing about whether something happened), and it is the mechanism
 * behind the BIA records that went missing in #5423 — fixed there by
 * serialising the wizard's own two callers, which left every OTHER caller of
 * this primitive (assets, vendors, findings, and the BIA delete path) exposed
 * to the same race from any other pair of concurrent writers.
 *
 * A fence closes it generally. `beginRecordWrite(key)` stamps a write with a
 * monotonic token before it is issued; `markRecordCommitted(key)` records that
 * a write for that key durably landed. `restoreRecord` then declines to roll
 * back at all when a commit for the same key happened after this write began —
 * because whatever is in state now came from the server, not from this call's
 * optimistic insert, and undoing it would discard a persisted record.
 *
 * This is deliberately conservative in one direction only: it can skip a
 * rollback that would have been harmless (leaving a correct server value in
 * place), and it can never remove a record the server is holding. Failing the
 * other way is the one that loses data.
 */

// ---------------------------------------------------------------------------
// Write fences
// ---------------------------------------------------------------------------
//
// One monotonic counter orders every begin and every commit across all
// collections, so "did a commit for this key happen after my write began?" is
// a single integer comparison and needs no timers or clocks.

let seq = 0;

/** key -> counter value at which a write for that key most recently committed. */
const committedAt = new Map();

// A session that touches thousands of records must not grow this map without
// bound. Entries only ever matter while a write for the same key is still in
// flight, so dropping the oldest ones is safe: a fence whose entry has been
// pruned simply falls back to the pre-fence behaviour (roll back), which is
// exactly what happens today.
const MAX_TRACKED_KEYS = 500;

function prune() {
  if (committedAt.size <= MAX_TRACKED_KEYS) return;
  // Map preserves insertion order, but a re-commit of an existing key updates
  // in place rather than re-inserting, so sort by counter to drop the genuinely
  // oldest.
  const byAge = [...committedAt.entries()].sort((a, b) => a[1] - b[1]);
  for (let i = 0; i < byAge.length - MAX_TRACKED_KEYS; i += 1) {
    committedAt.delete(byAge[i][0]);
  }
}

/**
 * Stamp a write before it is issued. Pass the returned fence to
 * `restoreRecord` so a rollback can tell whether a sibling write for the same
 * record committed while this one was in flight.
 *
 * @param {string} key  stable per-record key, e.g. `asset:<id>` — the SAME key
 *                      every writer for that record uses.
 * @returns {{ key: string | null, at: number }}
 */
export function beginRecordWrite(key) {
  seq += 1;
  return { key: key || null, at: seq };
}

/**
 * Record that a write for `key` durably landed. Call this once the server has
 * acknowledged, before or alongside reconciling the server's copy into state.
 */
export function markRecordCommitted(key) {
  if (!key) return;
  seq += 1;
  committedAt.set(key, seq);
  prune();
}

/**
 * True when a write for this fence's key committed after the fence was taken —
 * i.e. what is in state for that record now came from the server via a sibling
 * call, not from this call's optimistic update.
 */
export function committedSinceFence(fence) {
  if (!fence || !fence.key) return false;
  const at = committedAt.get(fence.key);
  return at !== undefined && at > fence.at;
}

/** Test hook. */
export function resetRecordWriteFences() {
  committedAt.clear();
  seq = 0;
}

/**
 * Capture the current value of one record so a failed write can put it back.
 *
 * Returns null when the record is not present — i.e. this was a create, and
 * rolling back means removing it again.
 *
 * @param {unknown} list      the collection as it stands before the optimistic write
 * @param {unknown} id        the record's identifier
 * @param {string} [idKey]    the identity field (defaults to 'id')
 * @returns {{ index: number, record: object } | null}
 */
export function captureRecord(list, id, idKey = 'id') {
  const arr = Array.isArray(list) ? list : [];
  const index = arr.findIndex((r) => r?.[idKey] === id);
  if (index < 0) return null;
  try {
    return { index, record: JSON.parse(JSON.stringify(arr[index])) };
  } catch {
    // Unserialisable record — better to leave the optimistic value in place
    // than to restore a mangled one. Callers treat null as "was a create",
    // so signal "nothing captured" with a marker the restore ignores.
    return { index, record: null };
  }
}

/**
 * Put one record back the way `captureRecord` found it.
 *
 * @param {unknown} list      the collection as it stands after the failed write
 * @param {unknown} id        the record's identifier
 * @param {{ index: number, record: object | null } | null} snapshot
 *        what `captureRecord` returned; null means the record did not exist
 *        before the optimistic write and must be removed again
 * @param {string | { idKey?: string, fence?: { key: string|null, at: number } }} [opts]
 *        the identity field (defaults to 'id'), or an options object carrying
 *        it plus the `fence` taken from `beginRecordWrite` before this write
 *        was issued. A string is still accepted so existing callers that only
 *        need a custom idKey are unchanged.
 * @returns {Array} a new array with the record restored
 */
export function restoreRecord(list, id, snapshot, opts = 'id') {
  const { idKey = 'id', fence = null } = typeof opts === 'string' ? { idKey: opts } : opts || {};
  const arr = Array.isArray(list) ? list.slice() : [];
  const at = arr.findIndex((r) => r?.[idKey] === id);

  // A sibling write for this same record committed while ours was in flight,
  // so what is in state now is the server's copy, not our optimistic update.
  // Rolling back would discard a record the server is holding — the silent
  // loss this fence exists to prevent. Our caller still surfaces its own
  // error; it just no longer takes a persisted record down with it.
  if (committedSinceFence(fence)) return arr;

  // Nothing was captured (unserialisable) — leave state alone rather than
  // replacing a real record with a guess.
  if (snapshot && snapshot.record === null) return arr;

  if (!snapshot) {
    // The optimistic write created this record. Undo the insert.
    if (at >= 0) arr.splice(at, 1);
    return arr;
  }

  if (at >= 0) {
    arr[at] = snapshot.record;
    return arr;
  }

  // The optimistic write removed it (a delete). Put it back where it was,
  // clamped in case the array changed length while the write was in flight.
  arr.splice(Math.min(Math.max(snapshot.index, 0), arr.length), 0, snapshot.record);
  return arr;
}
